import { AbstractControl, ValidatorFn } from '@angular/forms';
import { criteria, operators } from '@app/library/models/criterias.model';
import moment from 'moment-timezone';

export const urlRegex =
  '^(http:\\/\\/www\\.|https:\\/\\/www\\.|http:\\/\\/|https:\\/\\/)?[a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]{2,5}(:[0-9]{1,5})?(\\/.*)?$';

export const mailtoRegex = '^mailto:([^\\?]*)?$';

export const hourWithMsRegex = '^[0-9][0-9]:[0-9][0-9]:[0-9][0-9]\\.[0-9][0-9][0-9]?$';

export const notOnlySpacesRegExp = /^(?!\s+$).+/;

export function emptyCriterionValidator(): ValidatorFn {
  return (control: AbstractControl): { error: boolean } | null => {
    const criterion = control.value;
    if (!criterion || !criterion.length) {
      return { error: true };
    }
    const hasNull = criterion.reduce((prev: any, current: any) => {
      if (current.operator === operators.BETWEEN) {
        return (
          prev ||
          !current.value ||
          (current.value['from'] === 0 && current.value['to'] === 0)
        );
      }
      if (
        current.field === criteria.PLAYTIME ||
        current.field === criteria.YEAR ||
        current.field === criteria.BPM
      ) {
        if (current.value === undefined || current.value === null) {
          return true;
        }

        return prev || `${current.value}`.toLocaleLowerCase().length < 1;
      }

      return prev || !current.value || `${current.value}`.toLocaleLowerCase().length < 1;
    }, false);

    return hasNull ? { error: true } : null;
  };
}

function isTextEquals(field: string): boolean {
  return field === criteria.YEAR || field === criteria.BPM || field === criteria.PLAYTIME;
}

function isValidValue(val: string | number | object): boolean {
  if (val === undefined || val === null) {
    return false;
  }
  if (typeof val === 'number') {
    return !isNaN(val);
  }
  if (typeof val === 'string') {
    return `${val}`.toLocaleLowerCase().length > 0;
  }
  if (typeof val === 'object') {
    return Object.keys(val).length > 0;
  }

  return false;
}

export function validCriterion(): ValidatorFn {
  return (control: AbstractControl): { error: boolean } | null => {
    const val = control.value;
    if (val.field === criteria.UPLOAD_DATE) {
      if (val.operator.id === operators.BETWEEN) {
        return !val.betweenValue || !val.betweenValue.startDate ? { error: true } : null;
      }

      return !val.value || !val.value.startDate ? { error: true } : null;
    }
    switch (val.operator.id) {
      case operators.CONTAINS:
        return val.chipsValue && val.chipsValue.length ? null : { error: true };
      case operators.EQUALS:
        if (isTextEquals(val.field)) {
          return isValidValue(val.value) ? null : { error: true };
        } else {
          return val.chipsValue && val.chipsValue.length ? null : { error: true };
        }
      case operators.BETWEEN:
        return isValidValue(val.betweenFirstValue) && isValidValue(val.betweenSecondValue)
          ? null
          : { error: true };
      default:
        return isValidValue(val.value) ? null : { error: true };
    }
  };
}

/**
 * Form validator for a time range like 08:00 <> 10:00
 *
 * @param compareToSibling Name of sibling form field corresponding to compared time
 * @param isStart Whether this form field is the start of the range or not
 */
export function timeRangeValidator(
  compareToSibling: string,
  isStart: boolean,
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.parent) {
      return null;
    }

    const siblingControl = control.parent.get(compareToSibling);

    if (!control.value || !siblingControl.value) {
      return null;
    }

    const selfTime = moment(control.value, 'HH:mm');
    const siblingTime = moment(siblingControl.value, 'HH:mm');

    const [start, end] = isStart ? [selfTime, siblingTime] : [siblingTime, selfTime];

    if (start.isSameOrAfter(end)) {
      return { invalidTimeRange: { value: control.value } };
    }

    if (siblingControl.invalid) {
      siblingControl.updateValueAndValidity();
    }

    return null;
  };
}

export function weekDaysValidator(isRepeatField = true): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.parent) {
      return null;
    }
    const weekdayControl = control.parent.get('weekdays');
    const repeatControl = control.parent.get('repeat');
    const frequencyControl = control.parent.get('frequency');
    if (isRepeatField) {
      weekdayControl.updateValueAndValidity();
      if (frequencyControl) {
        frequencyControl.updateValueAndValidity();
      }
      if (!control.value) {
        return null;
      }
    }
    if (
      repeatControl.value &&
      (!frequencyControl || (frequencyControl && frequencyControl.value?.id !== '1')) &&
      !weekdayControl.value?.size
    ) {
      return { invalidDays: { value: true } };
    }
    if (weekdayControl.invalid) {
      weekdayControl.updateValueAndValidity();
    }
    if (frequencyControl && frequencyControl.invalid) {
      frequencyControl.updateValueAndValidity();
    }

    if (!isRepeatField && repeatControl.invalid) {
      repeatControl.updateValueAndValidity();
    }

    return null;
  };
}

export function xUsernameValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) {
      return null;
    }
    if (control.value?.substring(0, 1) !== '@') {
      return { invalidUsername: true };
    }

    return null;
  };
}

export function labelOrIconValidator(otherField: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.parent) {
      return null;
    }
    const otherFieldControl = control.parent.get(otherField);
    if (
      (!control.value && !otherFieldControl.value) ||
      (!otherFieldControl.value &&
        typeof control.value === 'string' &&
        !control.value.trim())
    ) {
      return {
        labelOrIconRequired: true,
      };
    }

    return null;
  };
}
